// src/features/resident/screens/ResidentDashboard.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  AlertCircle,
  CalendarDays,
  CircleDot,
  Info,
  Megaphone,
  Building2,
  Mail,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useResidentProfile } from "@/hooks/useResidentProfile";
import {
  getPendingResidentMemberships,
  acceptResidentInvitation,
  type PendingMembership,
} from "@/api/residentAuth";
import { getMyInvoices } from "@/api/payments";

const PRIMARY = "#1F3A93";

function formatLocalizedDate(dateStr: string, locale: string) {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? `${dateStr}T00:00:00`
    : dateStr;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/* -------- Mock poll (replace with API later) -------- */
type Poll = {
  id: string;
  titleKey: string;
  closesAt: string;
  options: { id: string; labelKey: string; votes: number }[];
  totalVotes: number;
};
const latestPoll: Poll | null = {
  id: "poll-42",
  titleKey: "resident.dashboard.pollTitle",
  closesAt: "2024-03-30",
  options: [
    { id: "opt-yes", labelKey: "common.yes", votes: 64 },
    { id: "opt-no", labelKey: "common.no", votes: 36 },
  ],
  totalVotes: 100,
};

export default function ResidentDashboard() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { data: profileData, isLoading: isLoadingProfile } = useResidentProfile();
  const dateLocale = language === "es" ? "es-VE" : "en-US";

  // Pending invitations — memberships the HOA admin added but not yet claimed
  const { data: pendingData, refetch: refetchPending } = useQuery({
    queryKey: ["residentPendingMemberships"],
    queryFn: getPendingResidentMemberships,
    staleTime: 60 * 1000,
    retry: false,
  });
  const pendingMemberships = pendingData?.pending_memberships ?? [];

  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["myInvoices"],
    queryFn: getMyInvoices,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Derive payment status badge from real invoice data
  const hasOverdue = invoices.some(
    (inv) => (inv.status === "UNPAID" || inv.status === "SENT") && inv.is_overdue
  );
  const hasUnderReview = invoices.some((inv) => inv.status === "PENDING_VERIFICATION");
  const hasDue = invoices.some(
    (inv) => (inv.status === "UNPAID" || inv.status === "SENT") && !inv.is_overdue
  );
  const lastPaid = invoices
    .filter((inv) => inv.status === "PAID" && inv.verified_at)
    .sort((a, b) => new Date(b.verified_at!).getTime() - new Date(a.verified_at!).getTime())[0];
  const nextDue = invoices
    .filter((inv) => (inv.status === "UNPAID" || inv.status === "SENT") && inv.due_date)
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())[0];

  const [acceptingId, setAcceptingId] = React.useState<string | null>(null);
  const [acceptError, setAcceptError] = React.useState<string | null>(null);

  async function handleAcceptInvitation(membership: PendingMembership) {
    setAcceptingId(membership.resident_id);
    setAcceptError(null);
    try {
      await acceptResidentInvitation(membership.resident_id);
      navigate("/resident/select-property");
    } catch (err: any) {
      setAcceptError(
        err?.response?.data?.message || t("resident.dashboard.pendingAcceptError")
      );
    } finally {
      setAcceptingId(null);
    }
  }

  // Extract resident info
  const residentName = profileData?.data?.full_name || t("resident.dashboard.residentFallback");
  const unitCode = profileData?.data?.unit?.unit_code || "";

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Pending property invitations */}
      {pendingMemberships.length > 0 && (
        <div className="space-y-3">
          {acceptError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {acceptError}
            </div>
          )}
          {pendingMemberships.map((membership) => (
            <div
              key={membership.resident_id}
              className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="shrink-0 rounded-xl bg-blue-100 p-2.5">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-blue-900">
                  {t("resident.dashboard.pendingInvitationTitle")}
                </p>
                <p className="text-sm text-blue-700 mt-0.5">
                  <span className="font-medium">
                    {membership.building.name || t("profile.unknownBuilding")}
                  </span>
                  {" · "}
                  {t("profile.unit")} {membership.unit.unit_code || "—"}
                  {" · "}
                  {membership.role_display}
                </p>
              </div>
              <Button
                size="sm"
                disabled={acceptingId === membership.resident_id}
                onClick={() => handleAcceptInvitation(membership)}
                className="shrink-0 rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-4 h-9 font-medium disabled:opacity-60"
              >
                {acceptingId === membership.resident_id
                  ? t("common.loading")
                  : t("resident.dashboard.pendingAccept")}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Greeting */}
      <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Info className="w-5 h-5 text-gray-700" />
          </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-gray-900">
                {isLoadingProfile ? (
                  <span className="text-gray-400">{t("common.loading")}</span>
                ) : (
                  t("resident.dashboard.greeting", { name: residentName })
                )}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                {isLoadingProfile ? (
                  <span className="text-gray-400">{t("common.loading")}</span>
                ) : (
                  t("resident.dashboard.unitStatus", { unit: unitCode })
                )}
              </p>
            </div>
        </div>
      </Card>

      {/* Payment status (dynamic rail) */}
      {isLoadingInvoices ? (
        <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
          <div className="h-16 animate-pulse rounded-2xl bg-gray-100" />
        </Card>
      ) : hasOverdue ? (
        <Card variant="glass" rail railColor="border-red-500" railWidth="border-l-4" className="rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <h3 className="font-semibold text-lg tracking-tight text-gray-900">
              {t("resident.dashboard.paymentStatus")}
            </h3>
            <div className="inline-flex items-center gap-2 text-sm font-medium text-red-600">
              <AlertCircle className="w-4 h-4" />
              {t("resident.dashboard.overdue")}
            </div>
          </div>
          {nextDue && (
            <div className="mt-3">
              <div className="rounded-2xl px-4 py-3 text-sm bg-red-50 ring-1 ring-red-200/70">
                <div className="font-medium text-red-900">{t("resident.dashboard.nextPaymentDue")}</div>
                <div className="text-red-800/80">{formatLocalizedDate(nextDue.due_date, dateLocale)}</div>
              </div>
            </div>
          )}
        </Card>
      ) : hasUnderReview ? (
        <Card variant="glass" rail railColor="border-amber-500" railWidth="border-l-4" className="rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <h3 className="font-semibold text-lg tracking-tight text-gray-900">
              {t("resident.dashboard.paymentStatus")}
            </h3>
            <div className="inline-flex items-center gap-2 text-sm font-medium text-amber-700">
              <CircleDot className="w-4 h-4" />
              {t("resident.dashboard.underReview")}
            </div>
          </div>
        </Card>
      ) : hasDue ? (
        <Card variant="glass" rail railColor="border-amber-500" railWidth="border-l-4" className="rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <h3 className="font-semibold text-lg tracking-tight text-gray-900">
              {t("resident.dashboard.paymentStatus")}
            </h3>
            <div className="inline-flex items-center gap-2 text-sm font-medium text-amber-700">
              <CalendarDays className="w-4 h-4" />
              {t("resident.dashboard.paymentDue")}
            </div>
          </div>
          {nextDue && (
            <div className="mt-3">
              <div className="rounded-2xl px-4 py-3 text-sm bg-amber-50 ring-1 ring-amber-200/70">
                <div className="font-medium text-amber-900">{t("resident.dashboard.nextPaymentDue")}</div>
                <div className="text-amber-800/80">{formatLocalizedDate(nextDue.due_date, dateLocale)}</div>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <Card variant="glass" rail railColor="border-emerald-500" railWidth="border-l-4" className="rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <h3 className="font-semibold text-lg tracking-tight text-gray-900">
              {t("resident.dashboard.paymentStatus")}
            </h3>
            <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              {t("resident.dashboard.upToDate")}
            </div>
          </div>
          <div className="mt-3">
            <div className="rounded-2xl px-4 py-3 text-sm bg-emerald-50 ring-1 ring-emerald-200/70">
              {lastPaid ? (
                <>
                  <div className="font-medium text-emerald-900">{t("resident.dashboard.lastPaymentMade")}</div>
                  <div className="text-emerald-800/80">{formatLocalizedDate(lastPaid.verified_at!, dateLocale)}</div>
                </>
              ) : (
                <div className="font-medium text-emerald-900">{t("resident.dashboard.noPayments")}</div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Quick actions (responsive grid) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <ActionTile
          title={t("resident.dashboard.action.report")}
          subtitle={t("resident.dashboard.action.issue")}
          icon={<AlertCircle className="w-6 h-6" />}
          onClick={() => navigate("/resident/issues")}
        />
        <ActionTile
          title={t("resident.dashboard.action.view")}
          subtitle={t("resident.dashboard.action.payments")}
          icon={<CheckCircle2 className="w-6 h-6" />}
          onClick={() => navigate("/resident/payments")}
        />
      </div>

      {/* Latest announcement (amber rail; responsive header that wraps) */}
      <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Megaphone className="w-5 h-5 text-gray-700" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Header row that stacks on mobile */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              {/* Title + chip (wrap if long) */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("resident.dashboard.latestAnnouncement")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t("announcements.maintenance")}
                </Badge>
              </div>

              {/* Date moves to the right on ≥sm, drops under on mobile */}
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" />
                {t("date.daysAgo", { count: 2 })}
              </div>
            </div>

            <p className="text-gray-800 mt-2 leading-6 break-words">
              {t("resident.dashboard.announcementSummary")}
            </p>
          </div>
        </div>
      </Card>

      {/* Latest poll (same responsive header pattern) */}
      {latestPoll && (
        <div className="relative">
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-3xl backdrop-blur-sm bg-white/60">
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 ring-1 ring-black/10 shadow-xl px-8 py-7 text-center max-w-xs mx-4">
              <span className="text-4xl">🚧</span>
              <h2 className="text-xl font-semibold text-gray-900">{t("resident.dashboard.comingSoon")}</h2>
              <p className="text-sm text-gray-500 leading-relaxed">{t("resident.dashboard.pollsComingSoonDescription")}</p>
              <span className="inline-block rounded-full bg-[#1F3A93]/10 text-[#1F3A93] text-xs font-medium px-3 py-1">{t("resident.dashboard.inDevelopment")}</span>
            </div>
          </div>
          <div className="blur-[2px] pointer-events-none select-none">
            <LatestPollCard
              poll={latestPoll}
              onVote={() => navigate("/resident/announcements")}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Small pieces ---------- */

function ActionTile({
  title,
  subtitle,
  icon,
  onClick,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${title} ${subtitle}`}
      className={[
        "rounded-2xl text-left transition will-change-transform",
        "bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 hover:shadow-xl",
        "active:scale-[0.99] px-4 py-4 flex items-center gap-3",
      ].join(" ")}
    >
      <div className="shrink-0 rounded-2xl p-2 bg-white ring-1 ring-black/10 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{subtitle}</div>
      </div>
    </button>
  );
}

function LatestPollCard({
  poll,
  onVote,
}: {
  poll: Poll;
  onVote: () => void;
}) {
  const { t, language } = useLanguage();
  const dateLocale = language === "es" ? "es-VE" : "en-US";

  return (
    <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
          <CircleDot className="w-5 h-5 text-gray-700" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Responsive header */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
            <h3 className="font-semibold text-lg tracking-tight text-gray-900">
              {t("resident.dashboard.latestPoll")}
            </h3>
            <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
              <CalendarDays className="w-4 h-4" />
              {t("resident.dashboard.pollCloses")} {formatLocalizedDate(poll.closesAt, dateLocale)}
            </div>
          </div>

          <p className="mt-1 font-medium text-gray-900 break-words">{t(poll.titleKey)}</p>

          <div className="mt-3 space-y-2">
            {poll.options.map((opt) => {
              const pct =
                poll.totalVotes > 0
                  ? Math.round((opt.votes / poll.totalVotes) * 100)
                  : 0;
              return (
                <div key={opt.id}>
                  <div className="flex items-center justify-between text-sm text-gray-700">
                    <span className="truncate">{t(opt.labelKey)}</span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-100 ring-1 ring-black/5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-[width]"
                      style={{ width: `${pct}%`, background: PRIMARY }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 sm:justify-between text-sm text-gray-600">
            <span className="tabular-nums">{t("resident.dashboard.voteCount", { count: poll.totalVotes })}</span>
            <Button
              onClick={onVote}
              title={t("resident.dashboard.goToPolls")}
              className="rounded-2xl px-4 py-2.5 font-medium text-white shadow-lg shadow-black/10 hover:shadow-xl active:scale-[0.99] w-full sm:w-auto"
              style={{ backgroundColor: PRIMARY }}
            >
              {t("resident.dashboard.voteNow")}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
